import React, { useEffect, useState } from 'react';
import { List, ListItem, SearchInput, Title } from '@icarros/ids-react';
import { useAdContext } from '../../../store/contexts/ad';
import { ListItemModifier, OpenEditTitleWrapper, OpenEditWrapper } from './style';
import { useAd } from '../../../store/hooks/useAd';
import { Loading } from '../../../components/Loading';
import { SaveFormContent } from './SaveFormContent';
import { getFilteredContent } from '../../../utils/getFilteredContent';
import { ResultsNotFound } from '../../../components/ResultsNotFound';
import { useHistory, useLocation } from 'react-router-dom';
import { MultipleEdits } from './components/Modal';
import { EDIT_ROUTES } from '../../../utils/adEditRoutes';
import { AdActions } from '../../../store/actions/ad';
import { isLongList, isMediumList } from '../../../utils/boolean';
import { useQuery } from '@tanstack/react-query';
import { getVehicleVersions } from '../../../services/Edits.service';
import { GET_VEHICLE_VERSIONS } from '../../../queries';

export function VersionsForm(): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { invalidatePricesCache } = AdActions();
  const { setCurrentContent, loading, error } = useAd();
  const {
    currentAd,
    setValueIsDifferentFromCurrent,
    setCurrentWarningMessage,
    cumulativeChanges,
    setCurrentTabDepth
  } = useAdContext();
  const { versao, modelo, anoModelo } = currentAd.veiculo;
  const [selectedRadio, setSelectedRadio] = useState(
    cumulativeChanges?.veiculo?.versao?.id ? cumulativeChanges.veiculo?.versao.id : versao?.id
  );
  const [searchField, setSearchField] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const priceUrl = location.pathname.replace(EDIT_ROUTES.version.name, EDIT_ROUTES.price.name);

  const currentModelYear = cumulativeChanges?.veiculo?.anoModelo
    ? cumulativeChanges.veiculo?.anoModelo
    : anoModelo;

  const { data, isInitialLoading } = useQuery({
    queryKey: [GET_VEHICLE_VERSIONS, modelo.id, currentModelYear, anoModelo],
    queryFn: () =>
      getVehicleVersions({
        modelId: modelo.id,
        modelYear: currentModelYear
      })
  });

  const filteredVersions =
    searchField !== '' ? data && getFilteredContent(data, searchField) : data;

  useEffect(() => {
    setValueIsDifferentFromCurrent(selectedRadio !== versao?.id);
  }, [selectedRadio, versao?.id, setValueIsDifferentFromCurrent]);

  useEffect(() => {
    if (error?.fieldMissing) {
      setModalOpen(true);
      setCurrentWarningMessage({
        title: 'deseja confirmar as alterações?',
        body: 'Para salvar a alteração da versão do seu veículo você precisará confirmar também o seu valor de venda.',
        redirect: priceUrl
      });
    }
  }, [error, priceUrl, setCurrentWarningMessage]);

  return (
    <OpenEditWrapper>
      <OpenEditTitleWrapper>
        <Title size="overline">editar dados do veículo</Title>
        <Title variant="default" contrast="light">
          qual a versão do seu carro?
        </Title>
        {isLongList(data?.length) && (
          <SearchInput
            contrast="default"
            placeholder="buscar versão"
            onChange={event => setSearchField(event.target.value)}
          />
        )}
      </OpenEditTitleWrapper>
      {isInitialLoading && <Loading backgroundOnly={loading} />}

      {!isInitialLoading && data && (!filteredVersions || filteredVersions.length === 0) && (
        <ResultsNotFound />
      )}

      <List>
        {filteredVersions ? (
          filteredVersions.map(({ id, nome }) => {
            return (
              <ListItemModifier key={id} isNormalCase>
                <ListItem
                  label={nome}
                  variant="selector"
                  radioOnChange={() => setSelectedRadio(id)}
                  radioValue={id}
                  radioChecked={id === selectedRadio}
                  selectorName={nome}
                  selectorVariant="radio"
                />
              </ListItemModifier>
            );
          })
        ) : (
          <></>
        )}
      </List>

      {!(isInitialLoading && selectedRadio === versao?.id) && (
        <SaveFormContent
          loading={loading}
          isDisabled={selectedRadio === versao?.id}
          isBody={!isMediumList(data?.length)}
          handleClick={() => {
            const accumulated = cumulativeChanges ?? {};
            setCurrentContent({
              ...accumulated,
              veiculo: {
                ...accumulated.veiculo,
                versao: {
                  nome: data?.find(version => version.id === selectedRadio)?.nome,
                  id: selectedRadio
                }
              }
            });
          }}
        />
      )}

      <MultipleEdits
        title="deseja editar a versão do seu carro?"
        body="Ao editar a versão do seu veículo você precisará também atualizar o seu valor de venda."
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleContinue={() => {
          setCurrentTabDepth(state => state + 1);
          invalidatePricesCache();
          history.push(priceUrl);
        }}
        handleBack={() => {
          setModalOpen(false);
        }}
      />
    </OpenEditWrapper>
  );
}
